<template>
  <div class="error-page">
    <EPageError
      :theme="brandThemeStore.configuredTheme.error.themeName"
      :branded="brandThemeStore.configuredTheme.error.isBranded"
    >
      <template v-slot:title>{{ title }}</template>
      <template v-slot:description>{{ description }}</template>
      <template v-slot:actions>
        <VBtnDefault
          size="large"
          :to="backHomeUrl"
          :active="false"
        >
          {{ $t('errorPage.button.back') }}
        </VBtnDefault>
      </template>
    </EPageError>
  </div>
</template>

<script setup lang="ts">
import type { NuxtError } from '#app'

type ErrorStatusCode = '400' | '404' | '500'

const props = defineProps<{
  error?: NuxtError
}>()

const routePath = useRoutePath()
const brandsStore = useBrandsStore()
const brandThemeStore = useBrandThemeStore()
const route = useRoute()
const routeParams = useRouteParams(route)
const t = useTranslate()

const statusCode = computed<ErrorStatusCode>(() => {
  let statusCode = props.error?.statusCode ?? 500
  if (![400, 404, 500].includes(statusCode)) {
    statusCode = 500
  }
  return statusCode.toString() as ErrorStatusCode
})
useHead({
  title: useMetaTitle().error({
    statusCode: statusCode.value,
  }),
})

const title = computed(() => {
  return t(`errorPage.${statusCode.value}.title`)
})

const description = computed(() => {
  return t(`errorPage.${statusCode.value}.description`)
})

const backHomeUrl = computed(() => {
  const brandId = brandsStore.selectedBrandId
  const orderTypeResult = optionalRouteParamResult(routeParams.orderType)

  let orderType = brandsStore.defaultOrderType
  if (isOk(orderTypeResult) && orderTypeResult.data && brandsStore.allowedOrderTypes.includes(orderTypeResult.data)) {
    orderType = orderTypeResult.data
  }

  return routePath.landing({
    brandId,
    orderType,
  })
})
</script>

<style lang="scss" scoped>
@use '@fire-framework/styles/variables';

.error-page {
  display: flex;
  flex-direction: column;
  height: 100%;
}
</style>
